import React, { useState } from 'react';
import { 
  AppBar, 
  Toolbar,
  Button, 
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getBrandConfig } from '../config/branding';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import PeopleIcon from '@mui/icons-material/People';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import GamesIcon from '@mui/icons-material/Games';
import SchoolIcon from '@mui/icons-material/School';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';

const Navbar: React.FC = () => {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const brandConfig = getBrandConfig();

  const toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' || (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }
    setDrawerOpen(open);
  };

  const isActive = (path: string) => {
    if (location.pathname === path) {
      return true;
    }
    
    // For player detail pages like /player/123, highlight the Players nav item
    if (path === '/players' && location.pathname.startsWith('/player/')) {
      return true;
    }
    
    // For league detail pages like /league/123, highlight the Leagues nav item
    if (path === '/leagues' && location.pathname.startsWith('/league/')) {
      return true;
    }
    
    // For individual game details (usually accessed via a query param), keep Games highlighted
    if (path === '/games' && location.pathname === '/games') {
      return true;
    }
    
    // For study pages, highlight the Study nav item
    if (path === '/study' && location.pathname.startsWith('/study/')) {
      return true;
    }
    
    return false;
  };

  const navItems = [
    { path: '/', label: 'Home', icon: <HomeIcon /> },
    { path: '/players', label: 'Players', icon: <PeopleIcon /> },
    { path: '/leagues', label: 'Leagues', icon: <EmojiEventsIcon /> },
    { path: '/games', label: 'Games', icon: <GamesIcon /> },
    { path: '/study', label: 'Study', icon: <SchoolIcon /> }
  ];

  const drawer = (
    <Box
      sx={{ width: 250 }}
      role="presentation"
      onClick={toggleDrawer(false)}
      onKeyDown={toggleDrawer(false)}
    >
      <Box sx={{ p: 2, bgcolor: 'primary.dark', display: 'flex', alignItems: 'center', gap: 1 }}>
        <img 
          src={brandConfig.logo} 
          alt={`${brandConfig.name} Logo`} 
          style={{ height: '30px', transform: 'scale(1.5)' }}
        />
          {brandConfig.icons}
      </Box>
      <List>
        {navItems.map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton 
              component={RouterLink} 
              to={item.path}
              selected={isActive(item.path)}
            >
              <ListItemIcon>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      <List>
        {user ? (
          <>
            <ListItem disablePadding>
              <ListItemButton 
                component={RouterLink} 
                to="/admin"
                selected={isActive('/admin')}
              >
                <ListItemIcon>
                  <AdminPanelSettingsIcon />
                </ListItemIcon>
                <ListItemText primary="Admin Dashboard" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={() => signOut()}>
                <ListItemIcon>
                  <LogoutIcon />
                </ListItemIcon>
                <ListItemText primary="Logout" />
              </ListItemButton>
            </ListItem>
          </>
        ) : (
          <ListItem disablePadding>
            <ListItemButton 
              component={RouterLink} 
              to="/login"
              selected={isActive('/login')}
            >
              <ListItemIcon>
                <LoginIcon />
              </ListItemIcon>
              <ListItemText primary="Admin" />
            </ListItemButton>
          </ListItem>
        )}
      </List>
    </Box>
  );

  return (
    <>
      <AppBar 
        position="static" 
        sx={{ 
          background: brandConfig.appBarBg,
          boxShadow: '0 4px 30px rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(15px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
          height: '70px',
          display: 'flex',
          justifyContent: 'center',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: 'linear-gradient(90deg, rgba(48, 43, 99, 0.3), rgba(48, 43, 99, 0), rgba(48, 43, 99, 0.3))',
            backgroundSize: '200% 100%',
            animation: 'gradient 5s ease infinite',
            zIndex: 0,
          }
        }}
      >
        <Toolbar sx={{ position: 'relative', zIndex: 1 }}>
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={toggleDrawer(true)}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          
          <Box 
            component={RouterLink}
            to="/"
            sx={{ 
              flexGrow: 1, 
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <img 
              src={brandConfig.logo}
              alt={`${brandConfig.name} Logo`}
              style={{ 
                height: '40px',
                filter: 'drop-shadow(1px 1px 2px rgba(0,0,0,0.5))',
                transform: 'scale(1.5)',
                marginLeft: '10px'
              }} 
            />
              {brandConfig.icons}
          </Box>

          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {navItems.map((item) => (
                <Button 
                  key={item.path}
                  color="inherit" 
                  component={RouterLink} 
                  to={item.path}
                  sx={{ 
                    borderRadius: 2,
                    bgcolor: isActive(item.path) ? 'rgba(255, 255, 255, 0.1)' : 'transparent'
                  }}
                  startIcon={item.icon}
                >
                  {item.label}
                </Button>
              ))}
              
              {user ? (
                <>
                  <Button 
                    variant="contained" 
                    color="secondary"
                    component={RouterLink} 
                    to="/admin"
                    sx={{ borderRadius: 2 }}
                    startIcon={<AdminPanelSettingsIcon />}
                  >
                    Admin
                  </Button>
                  <Button 
                    color="inherit" 
                    onClick={() => signOut()}
                    sx={{ borderRadius: 2 }}
                    startIcon={<LogoutIcon />}
                  >
                    Logout
                  </Button>
                </>
              ) : (
                <Button 
                  variant="contained"
                  color="secondary"
                  component={RouterLink} 
                  to="/login"
                  sx={{ borderRadius: 2 }}
                  startIcon={<LoginIcon />}
                >
                  Admin
                </Button>
              )}
            </Box>
          )}
        </Toolbar>
      </AppBar>
      
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Navbar;